import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useToast,
  useColorModeValue,
  IconButton,
  Center,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Progress
} from '@chakra-ui/react';
import { FiArrowLeft, FiVideo, FiVolume2, FiFileText, FiPlay } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';

interface EpisodeData {
  id: string;
  title: string;
  episodeNumber: string;
  thumbnail: string;
  duration: string;
  description: string;
  videoUrl: string;
  audioUrl?: string;
  trailerUrl?: string;
  subtitleUrl?: string;
  seriesId: string;
  seasonId: string;
}

const EditEpisode: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { episodeId } = useParams<{ episodeId: string }>();
  const toast = useToast();
  
  const [episode, setEpisode] = useState<EpisodeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Form data
  const [formData, setFormData] = useState({
    title: '',
    episodeNumber: '',
    description: '',
    duration: ''
  });
  
  // File states
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [trailerFile, setTrailerFile] = useState<File | null>(null);
  const [subtitleFile, setSubtitleFile] = useState<File | null>(null);
  
  // File previews
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [audioPreview, setAudioPreview] = useState<string | null>(null);
  const [trailerPreview, setTrailerPreview] = useState<string | null>(null);
  const [subtitlePreview, setSubtitlePreview] = useState<string | null>(null);
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const loadEpisode = useCallback(async () => {
    try {
      setLoading(true);
      const seriesId = localStorage.getItem('currentSeriesId');
      if (seriesId && episodeId) {
        const savedEpisodes = localStorage.getItem(`episodes-${seriesId}`);
        if (savedEpisodes) {
          const episodesData = JSON.parse(savedEpisodes);
          const foundEpisode = episodesData.find((ep: EpisodeData) => ep.id === episodeId);
          if (foundEpisode) {
            setEpisode(foundEpisode);
            setFormData({
              title: foundEpisode.title,
              episodeNumber: foundEpisode.episodeNumber,
              description: foundEpisode.description,
              duration: foundEpisode.duration
            });
            setVideoPreview(foundEpisode.videoUrl);
            setAudioPreview(foundEpisode.audioUrl || '');
            setTrailerPreview(foundEpisode.trailerUrl || '');
            setSubtitlePreview(foundEpisode.subtitleUrl || '');
          } else {
            toast({
              title: 'Episode not found',
              description: 'The requested episode could not be found.',
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
            navigate(-1);
          }
        }
      }
    } catch (error) {
      console.error('Error loading episode:', error);
      toast({
        title: 'Error',
        description: 'Failed to load episode details.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [episodeId, navigate, toast]);

  useEffect(() => {
    if (episodeId) {
      loadEpisode();
    }
  }, [episodeId, loadEpisode]);

  const handleFileSelect = (type: string, file: File | null) => {
    if (!file) return;

    // Validate file type
    const validTypes: { [key: string]: string[] } = {
      video: ['video/mp4', 'video/avi', 'video/mov', 'video/wmv'],
      audio: ['audio/mp3', 'audio/wav', 'audio/aac', 'audio/ogg'],
      trailer: ['video/mp4', 'video/avi', 'video/mov', 'video/wmv'],
      subtitle: ['text/plain', 'application/octet-stream', 'text/vtt', 'application/x-subrip']
    };

    if (validTypes[type] && !validTypes[type].includes(file.type) && file.type !== '') {
      toast({
        title: `Invalid ${type} file`,
        description: `Please select a valid ${type} file.`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Set file and preview
    switch (type) {
      case 'video':
        setVideoFile(file);
        setVideoPreview(URL.createObjectURL(file));
        break;
      case 'audio':
        setAudioFile(file);
        setAudioPreview(URL.createObjectURL(file));
        break;
      case 'trailer':
        setTrailerFile(file);
        setTrailerPreview(URL.createObjectURL(file));
        break;
      case 'subtitle':
        setSubtitleFile(file);
        setSubtitlePreview(URL.createObjectURL(file));
        break;
    }
  };

  const handleSave = async () => {
    if (!episode || !user) return;

    try {
      setSaving(true);
      setUploadProgress(0);

      // For now, just update the episode data in localStorage
      // In a real app, you would upload files and update the database
      const seriesId = episode.seriesId;
      const savedEpisodes = localStorage.getItem(`episodes-${seriesId}`);
      
      if (savedEpisodes) {
        const episodesData = JSON.parse(savedEpisodes);
        const updatedEpisodes = episodesData.map((ep: EpisodeData) => {
          if (ep.id === episodeId) {
            return {
              ...ep,
              ...formData,
              // In a real app, you would update URLs after file upload
              // videoUrl: videoFile ? newVideoUrl : ep.videoUrl,
              // audioUrl: audioFile ? newAudioUrl : ep.audioUrl,
              // etc.
            };
          }
          return ep;
        });
        
        localStorage.setItem(`episodes-${seriesId}`, JSON.stringify(updatedEpisodes));
        
        toast({
          title: 'Episode updated',
          description: 'Episode details have been successfully updated.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        navigate(`/episode/${episodeId}`);
      }
    } catch (error) {
      console.error('Error updating episode:', error);
      toast({
        title: 'Error',
        description: 'Failed to update episode.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  if (!user) {
    return (
      <Center h="50vh">
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.500">
            Please log in to edit episodes.
          </Text>
          <Button onClick={() => navigate('/login')} colorScheme="purple">
            Log In
          </Button>
        </VStack>
      </Center>
    );
  }

  if (loading) {
    return (
      <Center h="50vh">
        <Text>Loading episode...</Text>
      </Center>
    );
  }

  if (!episode) {
    return (
      <Center h="50vh">
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.500">
            Episode not found.
          </Text>
          <Button onClick={() => navigate(-1)} colorScheme="purple">
            Go Back
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Header */}
      <Box p={4} borderBottom="1px solid" borderColor="gray.200">
        <HStack gap={4} align="center">
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            onClick={() => navigate(-1)}
            size="sm"
            bg="purple.100"
            _hover={{ bg: 'purple.200' }}
          />
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            Edit Episode
          </Text>
        </HStack>
      </Box>

      {/* Main Content */}
      <Box p={6} maxW="4xl" mx="auto">
        <VStack spacing={6} align="stretch">
          {/* Basic Information */}
          <Box bg={cardBg} borderRadius="lg" p={6} border="1px solid" borderColor="gray.200">
            <Text fontSize="lg" fontWeight="bold" color={textColor} mb={4}>
              Episode Information
            </Text>
            
            <VStack spacing={4} align="stretch">
              <HStack gap={4}>
                <FormControl flex="1">
                  <FormLabel color={labelColor}>Episode Title</FormLabel>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter episode title"
                  />
                </FormControl>
                <FormControl flex="1">
                  <FormLabel color={labelColor}>Episode Number</FormLabel>
                  <Input
                    value={formData.episodeNumber}
                    onChange={(e) => setFormData({ ...formData, episodeNumber: e.target.value })}
                    placeholder="e.g., 1, 2, 3"
                  />
                </FormControl>
              </HStack>
              
              <FormControl>
                <FormLabel color={labelColor}>Duration</FormLabel>
                <Input
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  placeholder="e.g., 24:30"
                />
              </FormControl>
              
              <FormControl>
                <FormLabel color={labelColor}>Description</FormLabel>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter episode description"
                  rows={4}
                />
              </FormControl>
            </VStack>
          </Box>

          {/* File Uploads */}
          <Box bg={cardBg} borderRadius="lg" p={6} border="1px solid" borderColor="gray.200">
            <Text fontSize="lg" fontWeight="bold" color={textColor} mb={4}>
              Media Files
            </Text>
            
            <VStack spacing={4} align="stretch">
              {/* Video File */}
              <FormControl>
                <FormLabel color={labelColor}>Video File</FormLabel>
                <HStack gap={3}>
                  <Input
                    type="file"
                    accept="video/*"
                    onChange={(e) => handleFileSelect('video', e.target.files?.[0] || null)}
                    display="none"
                    id="video-upload"
                  />
                  <Button
                    as="label"
                    htmlFor="video-upload"
                    leftIcon={<IconWrapper icon={FiVideo} size={16} />}
                    variant="outline"
                    cursor="pointer"
                  >
                    {videoFile ? videoFile.name : 'Select Video'}
                  </Button>
                  {videoPreview && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(videoPreview, '_blank')}
                    >
                      Preview
                    </Button>
                  )}
                </HStack>
              </FormControl>

              {/* Audio File */}
              <FormControl>
                <FormLabel color={labelColor}>Audio File (Optional)</FormLabel>
                <HStack gap={3}>
                  <Input
                    type="file"
                    accept="audio/*"
                    onChange={(e) => handleFileSelect('audio', e.target.files?.[0] || null)}
                    display="none"
                    id="audio-upload"
                  />
                  <Button
                    as="label"
                    htmlFor="audio-upload"
                    leftIcon={<IconWrapper icon={FiVolume2} size={16} />}
                    variant="outline"
                    cursor="pointer"
                  >
                    {audioFile ? audioFile.name : 'Select Audio (Optional)'}
                  </Button>
                  {audioPreview && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(audioPreview, '_blank')}
                    >
                      Preview
                    </Button>
                  )}
                </HStack>
                <Text fontSize="sm" color={labelColor} mt={1}>
                  Optional: Add a separate audio track for the episode
                </Text>
              </FormControl>

              {/* Trailer File */}
              <FormControl>
                <FormLabel color={labelColor}>Trailer File (Optional)</FormLabel>
                <HStack gap={3}>
                  <Input
                    type="file"
                    accept="video/*"
                    onChange={(e) => handleFileSelect('trailer', e.target.files?.[0] || null)}
                    display="none"
                    id="trailer-upload"
                  />
                  <Button
                    as="label"
                    htmlFor="trailer-upload"
                    leftIcon={<IconWrapper icon={FiPlay} size={16} />}
                    variant="outline"
                    cursor="pointer"
                  >
                    {trailerFile ? trailerFile.name : 'Select Trailer'}
                  </Button>
                  {trailerPreview && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(trailerPreview, '_blank')}
                    >
                      Preview
                    </Button>
                  )}
                </HStack>
              </FormControl>

              {/* Subtitle File */}
              <FormControl>
                <FormLabel color={labelColor}>Subtitle File (Optional)</FormLabel>
                <HStack gap={3}>
                  <Input
                    type="file"
                    accept=".srt,.vtt,.txt,.sub,text/plain,application/octet-stream"
                    onChange={(e) => handleFileSelect('subtitle', e.target.files?.[0] || null)}
                    display="none"
                    id="subtitle-upload"
                  />
                  <Button
                    as="label"
                    htmlFor="subtitle-upload"
                    leftIcon={<IconWrapper icon={FiFileText} size={16} />}
                    variant="outline"
                    cursor="pointer"
                  >
                    {subtitleFile ? subtitleFile.name : 'Select Subtitle'}
                  </Button>
                  {subtitlePreview && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(subtitlePreview, '_blank')}
                    >
                      Preview
                    </Button>
                  )}
                </HStack>
              </FormControl>

            </VStack>
          </Box>

          {/* Save Button */}
          <HStack justify="flex-end" gap={3}>
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleSave}
              isLoading={saving}
              loadingText="Saving..."
            >
              Save Changes
            </Button>
          </HStack>

          {/* Progress Bar */}
          {saving && uploadProgress > 0 && (
            <Box>
              <Text fontSize="sm" color={labelColor} mb={2}>
                Uploading files... {uploadProgress}%
              </Text>
              <Progress value={uploadProgress} colorScheme="purple" />
            </Box>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default EditEpisode;
